import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Image } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { format, isValid, parse } from "date-fns";

import avatarImage from "../../assets/images/avatar.svg";
import { addNote } from "../../Redux/notes/notesSlice";
import { api_without_cancellation } from "../../api/api";
import { addClickRecord, getCaseId, getClientId } from "../../Utils/helper";

import NoteAddModalTreatment from "./NotesAddModalTreatment";
import TreatmentDescriptionNoteModal from "./TreatmentDescriptionNoteModal";
import "./notesTreatment.css";

const noteDate = (value) => {
  if (!value) return null;
  const formattedDate = parse(value, "MM/dd/yyyy hh:mm a", new Date());
  if (isValid(formattedDate)) return formattedDate;
  const fallbackDate = new Date(value);
  return isValid(fallbackDate) ? fallbackDate : null;
};

function TreatmentNotesSection({
  entity_type = "Insurance",
  instanceFor = "",
  record_id,
  module,
  pagePanels = 4,
  caseProvider = {},
  minimumRows,
}) {
  const node_env = process.env.NODE_ENV;
  const media_origin = node_env === "production" ? "" : process.env.REACT_APP_BACKEND_URL;
  const page_id_click_record = useSelector(
    (state) => state.page_id_click_record.page_id_click_record
  );
  const [showNotes, setShowNotes] = useState(false);
  const [data, setData] = useState([]);
  const [treatmentDescription, setTreatmentDescription] = useState("");
  const dispatch = useDispatch();
  const notesSectionRef = useRef(null);
  const treatmentNoteTextAreaRef = useRef(null);
  const treatmentNoteTextRef = useRef(null);
  const [treatmentNoteLines, setTreatmentNoteLines] = useState(0);
  const { pages } = useSelector((state) => state.notes);

  const handleProfilePic = (note) => {
    let profilePicUrl;

    try {
      profilePicUrl = note.created_by?.bp_attorneystaff_userprofile?.profile_pic_29p
        ? media_origin + note.created_by.bp_attorneystaff_userprofile.profile_pic_29p
        : note.created_by?.bp_attorneystaff_userprofile?.profile_pic_19p
          ? media_origin + note.created_by.bp_attorneystaff_userprofile.profile_pic_19p
          : avatarImage;
    } catch (error) {
      profilePicUrl = avatarImage;
    }
    return profilePicUrl;
  };

  const regex = /\d+/g;
  const { pathname } = useLocation();
  const numbers = pathname.match(regex);
  const URLParams = numbers?.map(Number);

  const setNotesResponse = (responseData) => {
    setData(
      Array.isArray(responseData)
        ? responseData
        : Array.isArray(responseData?.notes)
          ? responseData.notes
          : []
    );
    if (instanceFor === "Treatment") {
      setTreatmentDescription(
        typeof responseData?.notes_description === "string" ? responseData.notes_description : ""
      );
    }
  };

  const fetchData = async () => {
    try {
      const response = await api_without_cancellation.get(
        `/api/notes/${entity_type}/${record_id}`,
        {
          params: instanceFor === "Treatment" ? { case_provider: true } : {},
        }
      );
      setNotesResponse(response.data);
    } catch (error) {
      console.error("Error fetching notes panel data:", error);
    }
  };

  const refetchData = async ({ entity_type, record_id }) => {
    try {
      const response = await api_without_cancellation.get(
        `/api/notes/${entity_type}/${record_id}`,
        {
          params: instanceFor === "Treatment" ? { case_provider: true } : {},
        }
      );
      setNotesResponse(response.data);
    } catch (error) {
      console.error("Error fetching notes panel data:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [entity_type, record_id, module, pagePanels, instanceFor]);

  const handleFormSubmission = async (category, description) => {
    addClickRecord(999, page_id_click_record);
    await dispatch(
      addNote({
        client_id: instanceFor === "Treatment" ? getClientId() : URLParams[0],
        case_id: instanceFor === "Treatment" ? getCaseId() : URLParams[1],
        category: category,
        entity_type: entity_type,
        record_id: record_id,
        description: description,
      })
    );
    // Refetching the updated Data after Post Request
    refetchData({ entity_type: entity_type, record_id: record_id });

    // Hidding the Modal in Form Submission
    setShowNotes(false);
  };

  const handleTreatmentSubmission = async ({ treatmentDescription, note }) => {
    addClickRecord(999, page_id_click_record);

    await dispatch(
      addNote({
        client_id: getClientId(),
        case_id: getCaseId(),
        category: "Treatment",
        entity_type,
        record_id,
        description: note,
        notes_description: treatmentDescription,
      })
    ).unwrap();
    await refetchData({ entity_type, record_id });
  };

  const handleClick = () => {
    setShowNotes(true);
    addClickRecord(999, page_id_click_record);
  };

  const handleClose = () => {
    setShowNotes(false);
    addClickRecord(999, page_id_click_record);
  };

  function getPanelClassName() {
    if (module === "Witnesses") {
      return "notes-height-witness";
    } else if (module === "Litigation") {
      return "notes-height-litigation";
    } else if (module === "Loans") {
      return "notes-height-witness";
    } else if (module === "Dog Bite") {
      return "notes-height-for-dog-bite";
    } else if (module === "Insurance2") {
      return "notes-height-for-insurance-card";
    } else if (module === "PropertyDamage") {
      return "notes-height-for-property-damage";
    } else if (module === "WrongDeath") {
      return "notes-height-for-wrong-death";
    } else if (module === "Accident") {
      return "notes-height-accident";
    } else if (module === "Employments") {
      return "notes-height-employment";
    } else if (module === "Reports") {
      return "notes-height-reports";
    } else if (pagePanels === 4 && module === "Defendants") {
      return "notes-height-for-defendants-2";
    } else if (pagePanels === 4 && instanceFor === "Defendants") {
      return "notes-height-for-defendants-2";
    } else if (instanceFor === "Treatment") {
      return "notes-height-treatment";
    } else if (pagePanels === 3 && module === "Counsel") {
      return "notes-height-for-3p-counsel";
    } else if (pagePanels === 3 && module === "Experts") {
      return "notes-height-for-3p-experts";
    } else if (pagePanels === 4) {
      return "notes-height-for-4p";
    } else if (pagePanels === 6 && module === "Insurance" && instanceFor === "Defendants") {
      return "notes-height-for-defendants-2";
    } else {
      return "notes-height-for-defendants-2";
    }
  }

  // Keep rowsToFill calculation for empty state
  const [rowsToFill, setRowsToFill] = useState(7);

  useEffect(() => {
    if (instanceFor === "Treatment") return;
    const handleResize = () => {
      if (notesSectionRef.current) {
        const tableHeight = notesSectionRef.current.clientHeight;
        const rowHeight = 21; // Each row is 25px
        const existingRows = data?.length || 0;
        const calculatedRowsToFill = Math.floor(tableHeight / rowHeight) - existingRows;
        setRowsToFill(calculatedRowsToFill > 0 ? calculatedRowsToFill : 0);
      }
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [notesSectionRef, data, instanceFor]);

  const fillerRowCount = Number.isInteger(minimumRows)
    ? Math.max(0, minimumRows - (data?.length || 0))
    : Math.max(1, rowsToFill);
  const latestNote =
    instanceFor === "Treatment"
      ? data
          .filter((note) => note.description?.trim())
          .reduce((latest, note) => {
            if (!latest) return note;
            const noteTime = noteDate(note.created_at)?.getTime() || 0;
            const latestTime = noteDate(latest.created_at)?.getTime() || 0;
            return noteTime > latestTime || (noteTime === latestTime && note.id > latest.id)
              ? note
              : latest;
          }, null)
      : null;

  useLayoutEffect(() => {
    if (instanceFor !== "Treatment" || !latestNote || !treatmentNoteTextAreaRef.current) return;

    const updateAvailableLines = () => {
      const textArea = treatmentNoteTextAreaRef.current;
      const noteText = treatmentNoteTextRef.current;
      if (!textArea || !noteText) return;
      const lineHeight = Number.parseFloat(window.getComputedStyle(noteText).lineHeight) || 18;
      setTreatmentNoteLines(Math.max(0, Math.floor(textArea.clientHeight / lineHeight)));
    };

    updateAvailableLines();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateAvailableLines);
      return () => window.removeEventListener("resize", updateAvailableLines);
    }
    const resizeObserver = new ResizeObserver(updateAvailableLines);
    resizeObserver.observe(treatmentNoteTextAreaRef.current);
    return () => resizeObserver.disconnect();
  }, [instanceFor, latestNote?.id, treatmentDescription]);

  return (
    <>
      {instanceFor === "Treatment" ? (
        <div
          className={`position-relative bg-speciality-4 ${getPanelClassName()} notes-height-treatment treatment-notes-display d-flex-1 pt-0 position-relative z-index-1`}
          onClick={handleClick}
          ref={notesSectionRef}
        >
          {caseProvider?.account_number?.trim() && (
            <p className="treatment-description-text">
              <strong>Account #: </strong>
              {caseProvider?.account_number}
            </p>
          )}
          {treatmentDescription.trim() && (
            <p className="treatment-description-text">
              <strong>Treatment Description: </strong>
              {treatmentDescription}
            </p>
          )}
          {latestNote && (
            <div className="treatment-note-text-area" ref={treatmentNoteTextAreaRef}>
              <p
                className="treatment-note-text"
                ref={treatmentNoteTextRef}
                style={
                  treatmentNoteLines > 0
                    ? { WebkitLineClamp: treatmentNoteLines }
                    : { display: "none" }
                }
              >
                <strong>Note: </strong>
                {latestNote.description}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div
          className={`position-relative ${getPanelClassName()} d-flex-1 p-0 position-relative z-index-1`}
          onClick={handleClick}
          ref={notesSectionRef}
          style={{
            overflow: "auto", // Enable scrolling
            maxHeight: "100%", // Ensure it doesn't exceed parent height
            scrollbarWidth: "none",
            // height: "100%",
          }}
        >
          <table className="table table-borderless table-striped table-treatment has-height-25">
            <tbody className="tbody-panels" style={{ zIndex: "2" }}>
              {data.length > 0 ? (
                // If we have data, render the notes
                <>
                  {data.map((note, index) => (
                    <tr
                      key={note.id}
                      className={`height-25-treatment ${index % 2 === 0 ? "bg-speciality-2" : "bg-speciality-4"}`}
                    >
                      <td className="serial-number height-21 td-autosize width-36">{index + 1}</td>
                      <td className="td-autosize height-21" id="new-notes-treatment-padding-imp">
                        {noteDate(note.created_at)
                          ? format(noteDate(note.created_at), "MMMM dd, yyyy")
                          : ""}
                      </td>
                      <td className="td-autosize height-21" id="new-notes-treatment-padding-imp">
                        {noteDate(note.created_at)
                          ? format(noteDate(note.created_at), "h:mm a")
                          : ""}
                      </td>
                      <td className="td-autosize height-21" id="new-notes-treatment-padding-imp">
                        <span
                          className="d-flex align-items-center"
                          style={{
                            gap: "5px",
                          }}
                        >
                          <span className="ic ic-avatar ic-15 has-avatar-icon has-cover-img">
                            <Image src={handleProfilePic(note)} alt="" className="" />
                          </span>
                          {note.created_by?.first_name} {note.created_by?.last_name}
                        </span>
                      </td>
                      <td
                        className="INS-color-white-space-word-wrap"
                        style={{ textAlign: "left" }}
                        id="new-notes-treatment-padding-imp"
                      >
                        <p
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            whiteSpace: "normal",
                            margin: 0,
                          }}
                        >
                          {`${entity_type} Note: `}
                          {note.description}
                        </p>
                      </td>
                    </tr>
                  ))}
                  {Array.from({
                    length: fillerRowCount,
                  }).map((_, index) => (
                    <tr
                      key={`filler-${index}`}
                      className={`height-25-treatment fake-rows-new-provider ${
                        index % 2 === 0 ? "bg-speciality-4" : "bg-speciality-2"
                      }`}
                    >
                      <td colSpan={12} />
                    </tr>
                  ))}
                </>
              ) : (
                // If no data, fill with empty rows
                Array.from({ length: fillerRowCount }).map((_, index) => (
                  <tr
                    key={index}
                    className={`height-25-treatment fake-rows-new-provider height-25-treatment ${index % 2 === 0 ? "bg-speciality-2" : "bg-speciality-4"}`}
                  >
                    <td colSpan={12} />
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      {module === "Litigation" && (
        <div className="">
          <a
            href="#"
            style={{ backgroundColor: "var(--primary-10) !important" }}
            className={
              "btn btn-primary-lighter-2 mt-0 btn-hover-contact-panel-row font-weight-semibold text-lg height-25 rounded-0 d-flex align-items-center justify-content-center p-l-6 p-r-5"
            }
            onClick={handleClick}
          >
            Add New Note
          </a>
        </div>
      )}

      {showNotes && instanceFor === "Treatment" && (
        <TreatmentDescriptionNoteModal
          show={showNotes}
          initialDescription={treatmentDescription}
          onSave={handleTreatmentSubmission}
          handleClose={handleClose}
        />
      )}
      {showNotes && instanceFor !== "Treatment" && (
        <NoteAddModalTreatment
          show={showNotes}
          data={data}
          handleFormSubmission={handleFormSubmission}
          handleClose={handleClose}
          pages={pages}
          pannel={entity_type}
          module={module}
          caseProvider={caseProvider}
        />
      )}
    </>
  );
}

// Add CSS to your notesTreatment.css file or make sure these styles are applied:
/*

*/

export default TreatmentNotesSection;
